"use client";

import { useCallback, useLayoutEffect, useRef } from "react";
import { MarkReadType } from "@cabal/shared/domain/session/product/enums.js";
import { ChatMode } from "@/types/frontend-types";
import {
  ChatUnreadDiagnosticBranch,
  ChatUnreadDiagnosticComponent,
  ChatUnreadDiagnosticEvent,
  ChatUnreadDiagnosticPhase,
  LogLevel,
  logChatUnreadDiagnostic,
  normalizeChatUnreadError,
} from "@/utils/chatUnreadDiagnostics";
import type { SessionCollab } from "./context";
import {
  CHAT_PAGE_LIMIT,
  toSessionCollab,
} from "./session-data-provider-utils";
import type {
  SessionChatActionDependencies,
  SessionChatActions,
} from "./use-session-chat-actions.types";

type SessionChatModeActions = Pick<
  SessionChatActions,
  | "setChatMode"
  | "setChatTargetVersion"
  | "setChatTargetMessage"
  | "setChatIndicatedMessage"
  | "clearChatTargets"
>;

type UseSessionChatModeActionsInput = Pick<
  SessionChatActionDependencies,
  | "api"
  | "sessionId"
  | "chat"
  | "collabsByProduct"
  | "setChat"
  | "replaceProductCollabItems"
  | "updateLocalProductReadMarker"
  | "markAllCollabsAsRead"
  | "refreshProducts"
>;

interface MarkLeavingChatAsReadInput {
  previousMode: ChatMode;
  previousProductId: string | null;
}

interface ChatModeTargetInput {
  mode: ChatMode;
  productId?: string;
}

export function useSessionChatModeActions({
  api,
  sessionId,
  chat,
  collabsByProduct,
  setChat,
  replaceProductCollabItems,
  updateLocalProductReadMarker,
  markAllCollabsAsRead,
  refreshProducts,
}: UseSessionChatModeActionsInput): SessionChatModeActions {
  const setChatMode = useSetChatModeAction({
    api,
    sessionId,
    chat,
    collabsByProduct,
    setChat,
    replaceProductCollabItems,
    updateLocalProductReadMarker,
    markAllCollabsAsRead,
    refreshProducts,
  });
  const targetActions = useChatTargetActions({ setChat });

  return {
    setChatMode,
    ...targetActions,
  };
}

function useSetChatModeAction(
  input: UseSessionChatModeActionsInput,
): SessionChatModeActions["setChatMode"] {
  const markLeavingChatAsRead = useMarkLeavingChatAsRead(input);
  const loadProductCollabsForChatMode = useLoadProductCollabsForChatMode(input);
  const markProductCollabsRead = useMarkProductCollabsRead(input);
  const chatMode = input.chat.mode;
  const chatSelectedProductId = input.chat.selectedProductId;
  const { setChat } = input;

  return useCallback(
    async (mode: ChatMode, productId?: string): Promise<void> => {
      logChatUnreadDiagnostic({
        component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
        event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
        props: {
          phase: ChatUnreadDiagnosticPhase.SetChatModeStart,
          previousMode: chatMode,
          previousProductId: chatSelectedProductId ?? null,
          nextMode: mode,
          nextProductId: productId ?? null,
        },
      });

      markLeavingChatAsRead({
        previousMode: chatMode,
        previousProductId: chatSelectedProductId,
      });

      const selectedProductId = selectChatModeProductTarget({
        mode,
        productId,
      });
      setChat((previous) => ({
        ...previous,
        mode,
        selectedProductId,
        targetVersionId: null,
        targetMessageId: null,
        targetRequestId: previous.targetRequestId,
        indicatedMessageId: null,
        indicationRequestId: previous.indicationRequestId,
      }));

      if (!selectedProductId) {
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
          event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
          props: {
            phase: ChatUnreadDiagnosticPhase.SetChatModeNoProductTarget,
            previousMode: chatMode,
            nextMode: mode,
          },
        });
        return;
      }

      await loadProductCollabsForChatMode(selectedProductId);
      await markProductCollabsRead(selectedProductId);
    },
    [
      chatMode,
      chatSelectedProductId,
      loadProductCollabsForChatMode,
      markLeavingChatAsRead,
      markProductCollabsRead,
      setChat,
    ],
  );
}

function useMarkLeavingChatAsRead({
  markAllCollabsAsRead,
}: Pick<UseSessionChatModeActionsInput, "markAllCollabsAsRead">): (
  input: MarkLeavingChatAsReadInput,
) => void {
  return useCallback(
    (input: MarkLeavingChatAsReadInput): void => {
      // Leaving the MAIN chat must NOT mark it read here. This data-layer action
      // cannot see whether the main chat was actually visible — folded to the
      // chip vs open as a panel is page-layout state (desktopChatPlacement) that
      // only useSessionReadMarkers has. Marking it read on every mode switch
      // cleared unread while the chat was folded (never seen). Main-chat read is
      // owned solely by the visibility-gated delayed marker in
      // useSessionReadMarkers; here we only handle the product chat being left.
      const selectedBranch =
        input.previousMode === ChatMode.Product && input.previousProductId
          ? ChatUnreadDiagnosticBranch.MarkAllProductChat
          : ChatUnreadDiagnosticBranch.Skip;

      logChatUnreadDiagnostic({
        component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
        event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
        props: {
          phase: ChatUnreadDiagnosticPhase.MarkLeavingChatAsRead,
          selectedBranch,
          previousMode: input.previousMode,
          previousProductId: input.previousProductId,
        },
      });

      if (input.previousMode === ChatMode.Product && input.previousProductId) {
        markAllCollabsAsRead(input.previousProductId);
      }
    },
    [markAllCollabsAsRead],
  );
}

function useLoadProductCollabsForChatMode({
  api,
  sessionId,
  collabsByProduct,
  replaceProductCollabItems,
}: Pick<
  UseSessionChatModeActionsInput,
  "api" | "sessionId" | "collabsByProduct" | "replaceProductCollabItems"
>): (productId: string) => Promise<void> {
  return useCallback(
    async (productId: string): Promise<void> => {
      if (collabsByProduct[productId]) {
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
          event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
          props: {
            phase: ChatUnreadDiagnosticPhase.LoadProductCollabsSkipCached,
            sessionId,
            productId,
            collabCount: collabsByProduct[productId].length,
          },
        });
        return;
      }

      try {
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
          event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
          props: {
            phase: ChatUnreadDiagnosticPhase.LoadProductCollabsStart,
            sessionId,
            productId,
            limit: CHAT_PAGE_LIMIT,
          },
        });
        const response = await api.sessions.byId(sessionId).collabs({
          query: {
            productId,
            limit: CHAT_PAGE_LIMIT,
          },
        });
        const items = response.items.map((collab) =>
          toSessionCollab({ collab }),
        );
        replaceProductCollabItems({
          productId,
          items,
        });
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
          event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
          props: {
            phase: ChatUnreadDiagnosticPhase.LoadProductCollabsResolve,
            sessionId,
            productId,
            itemCount: items.length,
          },
        });
      } catch (error) {
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
          event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
          level: LogLevel.Error,
          props: {
            phase: ChatUnreadDiagnosticPhase.LoadProductCollabsReject,
            sessionId,
            productId,
            ...normalizeChatUnreadError(error),
          },
        });
      }
    },
    [api, collabsByProduct, replaceProductCollabItems, sessionId],
  );
}

function useMarkProductCollabsRead({
  api,
  sessionId,
  updateLocalProductReadMarker,
  refreshProducts,
}: Pick<
  UseSessionChatModeActionsInput,
  "api" | "sessionId" | "updateLocalProductReadMarker" | "refreshProducts"
>): (productId: string) => Promise<void> {
  const refreshProductsRef = useRef(refreshProducts);
  useLayoutEffect(() => {
    refreshProductsRef.current = refreshProducts;
  }, [refreshProducts]);

  return useCallback(
    async (productId: string): Promise<void> => {
      try {
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
          event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
          props: {
            phase: ChatUnreadDiagnosticPhase.MarkProductCollabsReadStart,
            sessionId,
            productId,
            markType: MarkReadType.Collabs,
          },
        });
        await api.sessions
          .byId(sessionId)
          .products.byId(productId)
          .markRead({ body: { mark: [MarkReadType.Collabs] } });
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
          event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
          props: {
            phase: ChatUnreadDiagnosticPhase.MarkProductCollabsReadResolve,
            sessionId,
            productId,
          },
        });
        updateLocalProductReadMarker({
          productId,
          collabsReadAt: new Date(),
        });
        void refreshProductsRef.current({ silent: true });
      } catch (error) {
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionChatModeActions,
          event: ChatUnreadDiagnosticEvent.ChatModeReadMarker,
          level: LogLevel.Error,
          props: {
            phase: ChatUnreadDiagnosticPhase.MarkProductCollabsReadReject,
            sessionId,
            productId,
            ...normalizeChatUnreadError(error),
          },
        });
      }
    },
    [api, sessionId, updateLocalProductReadMarker],
  );
}

function useChatTargetActions({
  setChat,
}: Pick<UseSessionChatModeActionsInput, "setChat">): Omit<
  SessionChatModeActions,
  "setChatMode"
> {
  const setChatTargetVersion = useCallback(
    (versionId: string | null): void => {
      setChat((prev) => ({
        ...prev,
        targetVersionId: versionId,
        targetMessageId: null,
        targetRequestId: prev.targetRequestId + 1,
      }));
    },
    [setChat],
  );

  const setChatTargetMessage = useCallback(
    (messageId: string | null): void => {
      setChat((prev) => ({
        ...prev,
        targetVersionId: null,
        targetMessageId: messageId,
        targetRequestId: prev.targetRequestId + 1,
      }));
    },
    [setChat],
  );

  const setChatIndicatedMessage = useCallback(
    (messageId: string | null): void => {
      setChat((previous) => ({
        ...previous,
        indicatedMessageId: messageId,
        indicationRequestId:
          messageId == null
            ? previous.indicationRequestId
            : previous.indicationRequestId + 1,
      }));
    },
    [setChat],
  );

  const clearChatTargets = useCallback((): void => {
    setChat((prev) => ({
      ...prev,
      targetVersionId: null,
      targetMessageId: null,
    }));
  }, [setChat]);

  return {
    setChatTargetVersion,
    setChatTargetMessage,
    setChatIndicatedMessage,
    clearChatTargets,
  };
}

function selectChatModeProductTarget({
  mode,
  productId,
}: ChatModeTargetInput): string | null {
  return mode === ChatMode.Product ? productId || null : null;
}
